import { useLayoutEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Skeleton } from '@voll-intelligence/ui-kit/ui/skeleton';
import { useNeighborPreload } from '../../shared/use_neighbor_preload.js';
import { HotelPhotoPlaceholder } from '../live_photos.js';
import type { HotelRoomView } from '../models.js';
import type { HotelRoomsCopy } from './hotel_rooms.js';

function revealVeil(veil: HTMLSpanElement | null): void {
  if (veil) veil.style.opacity = '0';
}

/** Foto remota com o esqueleto do kit por cima até o load. Revelar escreve
 *  direto no DOM (nenhum setState): `load` como update do React reiniciava
 *  renders concorrentes enquanto as fotos pingavam da rede — lição medida no
 *  drawer do design hub. */
function VeiledImage({
  src,
  alt,
  onError,
}: {
  readonly src: string;
  readonly alt: string;
  readonly onError: () => void;
}) {
  const image = useRef<HTMLImageElement>(null);
  const veil = useRef<HTMLSpanElement>(null);
  const reveal = () => {
    revealVeil(veil.current);
  };
  /* Cache quente (a vizinha que a pré-carga trouxe, #5616): a foto já está
     pronta no commit e o véu sai antes da primeira pintura, sem esmaecer. Num
     efeito de layout e não no `ref` da `img`: no mount o `ref` dela roda antes
     do `ref` do véu, que vem depois no DOM, e o revelar não achava o véu. */
  useLayoutEffect(() => {
    if (image.current?.complete) revealVeil(veil.current);
  }, []);
  return (
    <span className="absolute inset-0">
      <img
        ref={image}
        src={src}
        alt={alt}
        loading="lazy"
        className="animate-in fade-in size-full object-cover duration-300"
        onLoad={reveal}
        onError={() => {
          reveal();
          onError();
        }}
      />
      <span
        ref={veil}
        className="pointer-events-none absolute inset-0 transition-opacity duration-500"
      >
        <Skeleton className="size-full rounded-none" />
      </span>
    </span>
  );
}

/** Carrossel de fotos na extrema esquerda, na altura do card (`self-stretch`,
 *  nunca proporção fixa — a altura é do texto). Setas no hover (e em toque,
 *  onde hover não existe), pontos sempre: eles são o único indicador de que há
 *  mais de uma foto. */
export function RoomPhotoCarousel({
  photos,
  copy,
  open,
}: {
  readonly photos: HotelRoomView['photos'];
  readonly copy: HotelRoomsCopy;
  /** Painel de tarifas aberto? O canto de baixo da foto deixa de ser canto do
   *  card e desarredonda — arredondado ali desenha uma mordida branca. */
  readonly open: boolean;
}) {
  const [index, setIndex] = useState(0);
  /* A falha é POR FOTO, não do carrossel: uma URL quebrada não pode roubar as
     fotos boas do quarto (nem as setas e os pontos, que continuam por cima). */
  const [failed, setFailed] = useState<readonly string[]>([]);
  const photo = photos[index] ?? photos[0];
  const step = (delta: number) => {
    setIndex((current) => (current + delta + photos.length) % photos.length);
  };
  /* As vizinhas baixam quando o card entra na tela (#5616): o avanço sai do
     cache, sem esqueleto, e quarto fora da tela segue só com a foto `lazy`. */
  const frame = useRef<HTMLDivElement>(null);
  useNeighborPreload(
    photos.map(({ url }) => url),
    index,
    frame,
  );
  if (!photo) return null;
  const photoFailed = failed.includes(photo.id);
  return (
    <div
      ref={frame}
      className={`group/photo relative w-32 shrink-0 self-stretch overflow-hidden rounded-tl-xl @2xl:w-44 @max-xl:rounded-bl-none ${
        open ? '' : 'rounded-bl-xl'
      }`}
    >
      {photoFailed ? (
        <HotelPhotoPlaceholder className="absolute inset-0" />
      ) : (
        /* `key` no id: cada foto é um nó novo — sem ele o browser reusa a mesma
           `img` e a troca fica sem repaint em conexão lenta. */
        <VeiledImage
          key={photo.id}
          src={photo.url}
          alt={photo.alt}
          onError={() => {
            setFailed((current) => (current.includes(photo.id) ? current : [...current, photo.id]));
          }}
        />
      )}
      {photos.length > 1 ? (
        <>
          {[
            { side: 'left-1', delta: -1, Icon: ChevronLeft, label: copy.previousPhotoLabel },
            { side: 'right-1', delta: 1, Icon: ChevronRight, label: copy.nextPhotoLabel },
          ].map(({ side, delta, Icon, label }) => (
            <button
              key={side}
              type="button"
              aria-label={label}
              onClick={(event) => {
                event.stopPropagation();
                step(delta);
              }}
              /* `bg-card/85` e não branco puro: a superfície do sistema é creme,
                 e branco em cima de uma foto quente lê como adesivo. */
              className={`bg-card/85 text-foreground absolute ${side} top-1/2 grid size-6 -translate-y-1/2 place-items-center rounded-md opacity-0 shadow-sm transition-opacity group-hover/photo:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100`}
            >
              <Icon className="size-3.5" aria-hidden />
            </button>
          ))}
          <span className="absolute inset-x-0 bottom-1.5 flex justify-center gap-1">
            {photos.map((dot, position) => (
              <span
                key={dot.id}
                className={`size-1.5 rounded-full transition-colors ${
                  position === index ? 'bg-white' : 'bg-white/50'
                }`}
              />
            ))}
          </span>
        </>
      ) : null}
    </div>
  );
}
